import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ActivityEventType,
	type ActivityItemEdge,
	type ActivityNode,
	type ActivityProduct,
	cachedRecentActivitiesQuery,
	fetchJiraIssuesQuery,
	getProductAndIDFromARI,
	is1PActivity,
	isAssetsActivity,
	isDocumentActivity,
	isGithubActivity,
	isGraphActivity,
	isRemoteLinkActivity,
	type JiraIssueById,
	type JiraIssuesQueryVariables,
	QuickFindDataProduct,
	type QuickFindGithubEntity,
	type QuickFindRecentActivitiesQueryVariables,
	recentActivitiesQuery,
} from '@atlassian/search-client';
import { type ExperimentConfig } from '@atlassian/search-experiment';

import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { messages } from '../../../../common/ui/search-result-type/messages';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { serve3PClickIngestionExperiment } from '../../../../common/utils/experiments';
import type { RecentActivities } from '../types';

export const INCLUDED_PRODUCTS: string[] = ['confluence', 'jira', 'townsquare', 'graph'];

export const INCLUDED_OBJECT_TYPES: string[] = [
	'page',
	'blogpost',
	'whiteboard',
	'issue',
	'project',
	'goal',
	'content',
	'document',
	'remote-link',
];

/**
 * Event types to be included in the recent activities query.
 * Based upon the AVI actions for each product: https://bitbucket.org/atlassian/flight-deck/src/7bab054550927c3c023f2eb783ce9d2ca6279bfc/packages/integrations/worklens/contrib/
 */
const INCLUDED_EVENT_TYPES = [
	'assigned',
	'unassigned',
	'viewed',
	'updated',
	'created',
	'liked',
	'transitioned',
	'published',
	'edited',
	'commented',
	'sl-viewed',
	'sl-clicked',
	'shared',
	'exported',
	'deleted',
	'started',
	'snapshotted',
	'trashed',
	'reaction'
];

const ORS_BATCH_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';
const GRANTED = 'granted';
const RECENT_ACTIVITIES_LIMIT = 200;
const OK_STATUS = 200;

/**
 * Returns the list of products to be included in the recent activities query based on the experiment.
 * We do NOT manipulate INCLUDED_OBJECT_TYPES to keep the logic simple.
 *
 * https://switcheroo.atlassian.com/ui/gates/0cd2bdec-6ec9-49e4-9393-54e282cf472a/key/remove-jira-recents-from-quick-find-in-confluence
 */
const getIncludedProductsBasedOnCohort = (): string[] => {
	const COHORTS = {
		removeJira: 'remove-jira', // Test 1.
		confluenceOnly: 'confluence-only', // Test 2.
		control: 'control', // Control
	};

	const removeJiraRecentsExperimentCohort = FeatureGates.getExperimentValue<string>(
		'remove-jira-recents-from-quick-find-in-confluence',
		'cohort',
		'control',

		// We need to know the cohort to load the correct products, but we are NOT triggering the exposure
		// event yet since it would be too wide. Instead, we're doing that in src/ui/quick-find/dialog/index.tsx.
		// For the decision see https://hello.atlassian.net/wiki/spaces/SEARCH/pages/5615125165/Experiment+structure#Options.
		{ fireExperimentExposure: false },
	);

	if (fg('assets_rovo_search_all')) {
		INCLUDED_PRODUCTS.push('assets');
		INCLUDED_OBJECT_TYPES.push('object');
		INCLUDED_OBJECT_TYPES.push('object_type');
		INCLUDED_OBJECT_TYPES.push('schema');
	}

	if (removeJiraRecentsExperimentCohort === COHORTS.removeJira) {
		return INCLUDED_PRODUCTS.filter((product) => product !== 'jira');
	} else if (removeJiraRecentsExperimentCohort === COHORTS.confluenceOnly) {
		return ['confluence'];
	}

	return INCLUDED_PRODUCTS;
};

const getFilteredEventTypes = (thirdPartyConfigs?: ThirdPartyConfigsBootstrap): string[] => {
	if (thirdPartyConfigs && serve3PClickIngestionExperiment(thirdPartyConfigs, { fireExperimentExposure: false })) {
		return [...INCLUDED_EVENT_TYPES, 'clicked'];
	}

	return INCLUDED_EVENT_TYPES;
};

export const fetchRecentActivities = async (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	experimentConfig?: ExperimentConfig,
	after: string = '',
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	headers?: Record<string, string>,
) => {
	const includedProductsBasedOnCohort = getIncludedProductsBasedOnCohort();
	const recentActivitiesProductsFromCombinedExperiment =
		experimentConfig?.combinedExperiment?.enabled &&
		experimentConfig?.combinedExperiment?.uiFeatures?.recentActivitiesProducts;

	const overrideFilteredProducts =
		Array.isArray(recentActivitiesProductsFromCombinedExperiment) &&
		recentActivitiesProductsFromCombinedExperiment.every((p) => INCLUDED_PRODUCTS.includes(p))
			? recentActivitiesProductsFromCombinedExperiment
			: undefined;

	const variables: QuickFindRecentActivitiesQueryVariables = {
		filteredProducts: Array.from(
			new Set(overrideFilteredProducts || includedProductsBasedOnCohort),
		),
		filteredObjectTypes: Array.from(new Set(INCLUDED_OBJECT_TYPES)),
		filteredRootContainerIds: [
			`ari:cloud:platform::site/${cloudId}`,
			`ari:cloud:townsquare::site/${cloudId}`,
		],
		itemsLimit: 200,
		after,
		includeJiraIssueTypeAvatar: fg('rovo_search_jira_icons'),
		includeJiraIssueMetadata: fg('rovo_qf_jira_recents_metadata'),
		filteredEventTypes: getFilteredEventTypes(thirdPartyConfigs),
	};

	const serveAutorefreshCohort =
		FeatureGates.getExperimentValue<string>(
			'quick_find_autorefresh_experiment_v3',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		) === 'autorefresh';

	if (serveAutorefreshCohort) {
		return await cachedRecentActivitiesQuery({
			variables,
			aggAbsoluteUrl,
			headers,
			onlyRefreshIfExpired: true,
			returnExpired: false,
		}).then((response) => {
			const edges = response.data?.activity?.myActivity?.all?.edges || [];
			return edges.map(mapEssentialData);
		});
	} else {
		return await recentActivitiesQuery({
			variables,
			aggAbsoluteUrl,
			headers,
		}).then((response) => {
			const edges = response.data?.activity?.myActivity?.all?.edges || [];
			return edges.map(mapEssentialData);
		});
	}
};

export const resolveGithubRecentActivities = async (
	recentActivities: RecentActivities,
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => Promise<void>,
) => {
	const edges = sortEdgesByTimestamp(recentActivities.result.map((item) => item.data));
	const aris = getJiraAndConfluenceARIs(edges);
	const jiraIssues: JiraIssueById[] = await fetchJiraIssues(aris, aggAbsoluteUrl, logException);
	const githubLinkTimestampMap = buildTimestampMapFromJiraIssues(edges, jiraIssues);
	const githubLinks = Array.from(githubLinkTimestampMap.keys());
	const githubResolvedResponse = await objectResolverServiceBatchCall(githubLinks, logException);

	const githubEdges = mapResolvedGithubResponseToRecentActivities(
		githubResolvedResponse,
		githubLinkTimestampMap,
	);
	return githubEdges.map(mapEssentialData);
};

export const jiraIssuesQueryOperationName = 'QuickFindRecentActivitiesJiraIssuesQuery';
export const jiraIssuesQuery = `
    query ${jiraIssuesQueryOperationName}($jira_ids: [ID!]!) {
			jira_issuesByIds(ids: $jira_ids) {
				id,
				summary,
				descriptionField {
					description,
					richText {
						adfValue {
							convertedPlainText {
								plainText
							}
						}
					}
				}
			}
		}
  `;

export class JiraBackendApiError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'JiraBackendApiError';
	}
}

export const fetchJiraIssues = async (
	aris: string[],
	aggAbsoluteUrl: string | undefined,
	logException: (error: Error) => void,
): Promise<JiraIssueById[]> => {
	const jiraIssueAris = aris.filter((ari) => {
		try {
			const [resourceOwner, , resourceType] = getProductAndIDFromARI(ari);
			return resourceOwner === QuickFindDataProduct.JIRA && resourceType === 'issue';
		} catch {
			return false;
		}
	});

	if (jiraIssueAris.length === 0) {
		return [];
	}

	// From https://bitbucket.org/atlassian/graphql-central-schema/src/d0613f3cd3a46e91b2e91ad919ecc4c5a47140f0/schema/jira/gira/IssueView.nadel#lines-17
	// Only fetch first 100 items for experiment
	const limitedJiraIssueAris = jiraIssueAris.slice(0, 100);

	try {
		const result = await fetchJiraIssuesQuery({
			variables: { jira_ids: limitedJiraIssueAris } satisfies JiraIssuesQueryVariables,
			graphQuery: jiraIssuesQuery,
			aggAbsoluteUrl,
			operationName: jiraIssuesQueryOperationName,
		});
		return (result?.data?.jira_issuesByIds as JiraIssueById[]) || [];
	} catch (error) {
		logException(new JiraBackendApiError(`Failed to fetch jira issues: ${error}`));
		return [];
	}
};

export const mapEssentialData = (result: ActivityItemEdge) => {
	const product = result.node.object.product;
	let title = '';
	const timestamp = result.node?.event?.timestamp;

	if (is1PActivity(result.node.object)) {
		if (product === 'confluence') {
			title = result.node.object.data?.title || '';
		} else if (product === 'jira') {
			const key = result.node.object.data?.key;
			const summary = result.node.object.data?.fieldsById?.edges?.[0]?.node?.text;
			title = key && summary ? `${key}: ${summary}` : '';
		} else if (product === 'townsquare') {
			title = result.node.object.data?.name || '';
		}
	} else if (
		(isGraphActivity(result.node.object) &&
			(isDocumentActivity(result.node.object) || isRemoteLinkActivity(result.node.object))) ||
		isGithubActivity(result.node.object)
	) {
		title = result.node.object.data?.displayName || '';
	} else if (isAssetsActivity(result.node.object)) {
		title = result.node.object.data?.assetsLabel || '';
	} else {
		title = '';
	}

	return {
		id: result.node.object.id,
		title,
		data: result,
		type: 'recent-activity' as const,
		activityTimestamp: timestamp,
	};
};

export const getJiraAndConfluenceARIs = (edges: ActivityItemEdge[]): string[] => {
	let aris: string[] = [];
	edges.forEach((item) => {
		const ari = item.node.object.id;
		if (ari && (isConfluenceResult(ari) || isJiraResult(ari))) {
			aris.push(ari);
		}
	});
	return aris;
};

export const buildTimestampMapFromJiraIssues = (
	edges: ActivityItemEdge[],
	jiraIssuesById: JiraIssueById[],
): Map<string, string> => {
	const githubLinkTimestampMap = new Map<string, string>();

	jiraIssuesById.forEach((jiraIssue: JiraIssueById) => {
		if (jiraIssue?.id) {
			const correspondingEdge = edges.find((edge) => edge.node.object.id === jiraIssue.id);
			if (correspondingEdge) {
				const timestamp = correspondingEdge.node.event.timestamp;

				const githubLinks = scrapeGithubLinksFromSingleDocument(jiraIssue);
				githubLinks.forEach((link) => {
					githubLinkTimestampMap.set(link, timestamp);
				});
			}
		}
	});
	return githubLinkTimestampMap;
};

export const scrapeGithubLinksFromSingleDocument = (jiraIssue: JiraIssueById): string[] => {
	const githubLinks: Set<string> = new Set();
	const githubRegex =
		/https:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+(?:\/(?:issues\/\d+|pull\/\d+|commit\/[a-f0-9]+|blob\/[\w\-\.\/]+|tree\/[\w\-\.\/]+))?(?=\s|$|[^\w\-\/\.])/gi;

	const scanText = (text: string | undefined) => {
		if (!text) {
			return;
		}

		const matches = text.match(githubRegex);
		if (matches) {
			matches.forEach((link) => githubLinks.add(link));
		}
	};

	scanText(jiraIssue.summary);
	scanText(jiraIssue.descriptionField?.description);
	scanText(jiraIssue.descriptionField?.richText?.adfValue?.convertedPlainText?.plainText);

	return Array.from(githubLinks);
};

export class OrsApiError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'OrsApiError';
	}
}

export const objectResolverServiceBatchCall = async (githubLinks: string[], logException: (error: Error) => void): Promise<any[]> => {
	if (githubLinks.length === 0) {
		return [];
	}

	// https://bitbucket.org/atlassian/object-resolver-service/src/178ab83b4d54575d9ff2cd1246eb83a892b4fd6a/openapi.yaml#lines-335
	// Batch requests in chunks of 50 for experiment, with a maximum of 2 batches
	const BATCH_SIZE = 50;
	const MAX_BATCHES = 2;
	const allResults: any[] = [];

	const maxLinks = BATCH_SIZE * MAX_BATCHES;
	const linksToProcess = githubLinks.slice(0, maxLinks);

	for (let i = 0; i < linksToProcess.length; i += BATCH_SIZE) {
		const batch = linksToProcess.slice(i, i + BATCH_SIZE);
		const orsRequestData = batch.map((link: string) => ({
			resourceUrl: link,
		}));

		try {
			const response = await fetch(ORS_BATCH_ENDPOINT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-product': 'JiraQuickFindRecentsForGithub', // For tracking RPM in ORS API
				},
				body: JSON.stringify(orsRequestData),
			});

			if (response === null || !response.ok) {
				logException(new OrsApiError(`ORS API call failed: ${response.status} ${response.statusText}`));
			} else {
				const batchResults = await response.json();
				allResults.push(...batchResults);
			}
		} catch (error) {
			logException(new OrsApiError(`ORS API call error: ${error}`));
		}
	}

	return allResults;
};

export const mapResolvedGithubResponseToRecentActivities = (
	githubResolvedResponse: any[],
	githubLinkTimestampMap: Map<string, string>,
) => {
	const edges: ActivityItemEdge[] = [];
	githubResolvedResponse.forEach((resolvedResponse: any) => {
		if (
			resolvedResponse.status === OK_STATUS &&
			resolvedResponse.body.meta.access === GRANTED &&
			edges.length < RECENT_ACTIVITIES_LIMIT
		) {
			const originalTimestamp = githubLinkTimestampMap.get(resolvedResponse.body.data.url);
			const currentTimestamp = new Date().toISOString();
			const kebabCaseResourceType = camelToKebab(resolvedResponse.body.meta.resourceType);
			const typeMessage =
				kebabCaseResourceType && kebabCaseResourceType in messages
					? messages[kebabCaseResourceType as keyof typeof messages]
					: messages.unrecognised;

			const githubEdge: ActivityItemEdge = {
				node: {
					object: {
						data: {
							displayName: resolvedResponse.body.data.name || resolvedResponse.body.data.title,
							url: resolvedResponse.body.data.url,
						},
						id: sha256Hash(resolvedResponse.body.data['@id']), // Must hash id as it's ugc
						type: typeMessage.defaultMessage,
						product: 'github' as ActivityProduct,
					},
					event: {
						eventType: 'viewed' as ActivityEventType,
						timestamp: originalTimestamp || currentTimestamp,
					},
				} as ActivityNode<QuickFindGithubEntity>,
				cursor: '',
			};
			edges.push(githubEdge);
		}
	});

	return limitToMostRecentActivities(sortEdgesByTimestamp(edges), RECENT_ACTIVITIES_LIMIT);
};

const sortEdgesByTimestamp = (edges: ActivityItemEdge[]) => {
	return edges.sort((a, b) => {
		const timeA = new Date(a.node.event.timestamp).getTime();
		const timeB = new Date(b.node.event.timestamp).getTime();
		return timeB - timeA; // Descending order
	});
};

const limitToMostRecentActivities = (
	edges: ActivityItemEdge[],
	limit: number = 200,
): ActivityItemEdge[] => {
	return edges.slice(0, limit);
};

const isJiraResult = (ari: string) => {
	return ari.includes(':jira:');
};

const isConfluenceResult = (ari: string) => {
	return ari.includes(':confluence:');
};

/**
 * Converts camelCase string to kebab-case
 * e.g., 'pullRequest' -> 'pull-request'
 */
const camelToKebab = (str: string): string => {
	return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};
